import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import {
  HsHypersceneViewer,
  HsViewerInput,
  type HsHypersceneViewerOptions,
  type HsViewerInputOptions,
} from '@sibe/hyperscene';
import { KEY_MAP } from '../pages/hyperscene-new/utils/key-map.global';

/**
 * Base of the engine feature pages: an `HsHypersceneViewer` on the page's `#canvasDocument`, camera input,
 * per-frame animation and an FPS counter. Pages build their scene in `initScene`.
 */
@Directive()
export abstract class DemoViewerPage implements AfterViewInit, OnDestroy {
  @ViewChild('canvasDocument', { static: true })
  protected readonly canvasDocument: ElementRef<HTMLCanvasElement> | null = null;

  protected viewer: HsHypersceneViewer | null = null;

  public fps: number = 0;

  private input: HsViewerInput | null = null;
  private frames: number = 0;
  private framesSince: number = performance.now();

  constructor(protected readonly changeDetector: ChangeDetectorRef) {}

  /** Viewer options on top of the page defaults (continuous drawing on white). */
  protected viewerOptions(): Partial<HsHypersceneViewerOptions> {
    return {};
  }

  /** Camera input options, or null for a page without camera navigation. */
  protected inputOptions(): HsViewerInputOptions | null {
    return {};
  }

  protected abstract initScene(viewer: HsHypersceneViewer): void | Promise<void>;

  /** Moves objects before each frame; returns true when something moved. */
  protected animate(_viewer: HsHypersceneViewer): boolean {
    return false;
  }

  public ngAfterViewInit(): void {
    const canvas: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    if (canvas === undefined) {
      return;
    }
    const viewer: HsHypersceneViewer = new HsHypersceneViewer({
      canvas,
      continuous: true,
      clearColor: [1, 1, 1, 1],
      ...this.viewerOptions(),
      hooks: {
        beforeCulling: (): void => {
          // Moved objects need a new culling pass to stay visible.
          if (this.animate(viewer)) {
            viewer.invalidate();
          }
        },
      },
    });
    this.viewer = viewer;
    const inputOptions: HsViewerInputOptions | null = this.inputOptions();
    if (inputOptions !== null) {
      this.input = new HsViewerInput(viewer, { keyMap: KEY_MAP, ...inputOptions });
    }
    viewer.onFrame(() => this.countFrame());
    void this.initScene(viewer);
  }

  public ngOnDestroy(): void {
    this.input?.dispose();
    this.viewer?.dispose();
    this.viewer = null;
  }

  private countFrame(): void {
    this.frames++;
    const now: number = performance.now();
    if (now - this.framesSince > 1000) {
      this.fps = this.frames;
      this.frames = 0;
      this.framesSince = now;
      this.changeDetector.markForCheck();
    }
  }
}
